import { Player } from './Player';
/**
 * A TS wrapper for the Twitch interactive media player.
 */
export class TwitchPlayer {
    /**
     * Creates a new TwitchPlayer from a Player instance.
     * @param player The Player instance.
     * @constructor Creates a new TwitchPlayer instance.
     */
    static FromPlayer(player) {
        const twitchPlayer = new this();
        twitchPlayer._player = player;
        return twitchPlayer;
    }
    /**
     * Creates a new TwitchPlayer given a div element identifier and some options for the player.
     * @param divId The div element identiefier where the player will appear.
     * @param options The player options.
     * @constructor Creates a new TwitchPlayer instance.
     */
    static FromOptions(divId, options) {
        const twitchPlayer = new this();
        try {
            if (window.Twitch && window.Twitch.Player) {
                twitchPlayer._player = new window.Twitch.Player(divId, options);
            }
            else {
                console.warn('Player was created using the static file, from inside the package. ' +
                    'Please add the Twitch.Embed script to *index.html*, if you want to download the script directly from Twitch.');
                twitchPlayer._player = new Player(divId, options);
            }
        }
        catch (e) {
            console.exception('Player was created using the static file, from inside the package. ' +
                'Please add the Twitch.Player script to *index.html*, if you want to download the script directly from Twitch.', e);
            twitchPlayer._player = new Player(divId, options);
        }
        return twitchPlayer;
    }
    /**
     * Disables the captions for the content that is currently playing.
     * @deprecated Use with caution, not inculded in the official Twitch documentation.
     */
    disableCaptions() {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.disableCaptions();
    }
    /**
     * Enables the captions for the content that is currently playing.
     * @deprecated Use with caution, not inculded in the official Twitch documentation.
     */
    enableCaptions() {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.enableCaptions();
    }
    /**
     * Pauses the player.
     */
    pause() {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.pause();
    }
    /**
     * Begins playing the specified video.
     */
    play() {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.play();
    }
    /**
     * Seeks to the specified timestamp (in seconds) in the video and resumes playing if paused. Does not work for live streams.
     * @param timestamp The specified timestamp (in seconds).
     */
    seek(timestamp) {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.seek(timestamp);
    }
    /**
     * Sets the channel to be played.
     * @param channel The selected channel.
     */
    setChannel(channel) {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.setChannel(channel);
    }
    /**
     * Sets the channel to be played.
     * @param channelId The selected channel's identifier.
     */
    setChannelId(channelId) {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.setChannelId(channelId);
    }
    /**
     * Retrieves the playback statistics for this player.
     * The statistics contain information such as video FPS, resolution, latency and dropped frames.
     * @deprecated Use with caution, not inculded in the official Twitch documentation.
     */
    getPlaybackStatistics() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getPlaybackStats();
    }
    /**
     * Sets the collection to be played.
     * Optionally also specifies the video within the collection, from which to start playback.
     * If a video ID is not provided here or the specified video is not part of the collection,
     * playback starts with the first video in the collection.
     * @param collectionId The identifier for the collection.
     * @param videoId The identifier for the video.
     */
    setCollection(collectionId, videoId) {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.setCollection(collectionId, videoId);
    }
    /**
     * Sets the quality of the video. quality should be a string value returned by getQualities.
     * @param quality The quality to be set.
     */
    setQuality(quality) {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.setQuality(quality);
    }
    /**
     * Sets the video to be played to be played and starts playback at timestamp (in seconds).
     * @param videoID The identifier of the video to be played.
     * @param timestamp The spot where the playback will be started (in seconds).
     */
    setVideo(videoID, timestamp) {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.setVideo(videoID, timestamp);
    }
    /**
     * Returns true if the player is muted; otherwise, false.
     */
    getMuted() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getMuted();
    }
    /**
     * If true, mutes the player; otherwise, unmutes it. This is independent of the volume setting.
     * @param muted If true, player will be muted. Otherwise, it will be unmuted.
     */
    setMuted(muted) {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.setMuted(muted);
    }
    /**
     * Returns the volume level, a value between 0.0 and 1.0.
     */
    getVolume() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getVolume();
    }
    /**
     * Sets the volume to the specified volume level, a value between 0.0 and 1.0.
     * @param volumeLevel A number between 0 and 1.
     */
    setVolume(volumeLevel) {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.setVolume(volumeLevel);
    }
    /**
     * Returns the channel’s name. Works only for live streams, not VODs.
     */
    getChannel() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getChannel();
    }
    /**
     * Returns the channel’s identifier. Works only for live streams, not VODs.
     */
    getChannelId() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getChannelId();
    }
    /**
     * Returns the current video’s timestamp, in seconds. Works only for VODs, not live streams.
     */
    getCurrentTime() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getCurrentTime();
    }
    /**
     * Returns the duration of the video, in seconds. Works only for VODs,not live streams.
     */
    getDuration() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getDuration();
    }
    /**
     * Returns true if the live stream or VOD has ended; otherwise, false.
     */
    getEnded() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getEnded();
    }
    /**
     * Returns the available video qualities. For example, chunked (pass-through of the original source).
     */
    getQualities() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getQualities();
    }
    /**
     * Returns the current quality of video playback.
     */
    getQuality() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getQuality();
    }
    /**
     * Returns the video ID. Works only for VODs, not live streams.
     */
    getVideo() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getVideo();
    }
    /**
     * Returns true if the video is paused; otherwise, false. Buffering or seeking is considered playing.
     */
    isPaused() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.isPaused();
    }
    /**
     * Returns the name of the collection currently being played.
     */
    getCollection() {
        var _a;
        return (_a = this._player) === null || _a === void 0 ? void 0 : _a.getCollection();
    }
    /**
     * Adds an event listener for the given event.
     * @param event The event type to which the listener should react.
     * @param callback The logic that should happen when the listener fires.
     */
    addEventListener(event, callback) {
        var _a;
        (_a = this._player) === null || _a === void 0 ? void 0 : _a.addEventListener(event.toString(), callback);
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiVHdpdGNoUGxheWVyLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vLi4vc3JjL2xpYi9ldmVyeXRoaW5nL3ZpZGVvL1R3aXRjaFBsYXllci50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHQSxPQUFPLEVBQUUsTUFBTSxFQUFFLE1BQU0sVUFBVSxDQUFDO0FBSWxDOztHQUVHO0FBQ0gsTUFBTSxPQUFPLFlBQVk7SUFHdkI7Ozs7T0FJRztJQUNJLE1BQU0sQ0FBQyxVQUFVLENBQUMsTUFBYztRQUNyQyxNQUFNLFlBQVksR0FBRyxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ2hDLFlBQVksQ0FBQyxPQUFPLEdBQUcsTUFBTSxDQUFDO1FBQzlCLE9BQU8sWUFBWSxDQUFDO0lBQ3RCLENBQUM7SUFFRDs7Ozs7T0FLRztJQUNJLE1BQU0sQ0FBQyxXQUFXLENBQ3ZCLEtBQWEsRUFDYixPQUE0QjtRQUU1QixNQUFNLFlBQVksR0FBRyxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ2hDLElBQUk7WUFDRixJQUFVLE1BQU8sQ0FBQyxNQUFNLElBQVUsTUFBTyxDQUFDLE1BQU0sQ0FBQyxNQUFNLEVBQUU7Z0JBQ3ZELFlBQVksQ0FBQyxPQUFPLEdBQUcsSUFBVSxNQUFPLENBQUMsTUFBTSxDQUFDLE1BQU0sQ0FBQyxLQUFLLEVBQUUsT0FBTyxDQUFDLENBQUM7YUFDeEU7aUJBQU07Z0JBQ0wsT0FBTyxDQUFDLElBQUksQ0FDVixxRUFBcUU7b0JBQ25FLDhHQUE4RyxDQUNqSCxDQUFDO2dCQUNGLFlBQVksQ0FBQyxPQUFPLEdBQUcsSUFBSSxNQUFNLENBQUMsS0FBSyxFQUFFLE9BQU8sQ0FBQyxDQUFDO2FBQ25EO1NBQ0Y7UUFBQyxPQUFPLENBQUMsRUFBRTtZQUNWLE9BQU8sQ0FBQyxTQUFTLENBQ2YscUVBQXFFO2dCQUNuRSwrR0FBK0csRUFDakgsQ0FBQyxDQUNGLENBQUM7WUFDRixZQUFZLENBQUMsT0FBTyxHQUFHLElBQUksTUFBTSxDQUFDLEtBQUssRUFBRSxPQUFPLENBQUMsQ0FBQztTQUNuRDtRQUNELE9BQU8sWUFBWSxDQUFDO0lBQ3RCLENBQUM7SUFFRDs7O09BR0c7SUFDSSxlQUFlOztRQUNwQixNQUFBLElBQUksQ0FBQyxPQUFPLDBDQUFFLGVBQWUsR0FBRztJQUNsQyxDQUFDO0lBRUQ7OztPQUdHO0lBQ0ksY0FBYzs7UUFDbkIsTUFBQSxJQUFJLENBQUMsT0FBTywwQ0FBRSxjQUFjLEdBQUc7SUFDakMsQ0FBQztJQUVEOztPQUVHO0lBQ0ksS0FBSzs7UUFDVixNQUFBLElBQUksQ0FBQyxPQUFPLDBDQUFFLEtBQUssR0FBRztJQUN4QixDQUFDO0lBRUQ7O09BRUc7SUFDSSxJQUFJOztRQUNULE1BQUEsSUFBSSxDQUFDLE9BQU8sMENBQUUsSUFBSSxHQUFHO0lBQ3ZCLENBQUM7SUFFRDs7O09BR0c7SUFDSSxJQUFJLENBQUMsU0FBaUI7O1FBQzNCLE1BQUEsSUFBSSxDQUFDLE9BQU8sMENBQUUsSUFBSSxDQUFDLFNBQVMsRUFBRTtJQUNoQyxDQUFDO0lBRUQ7OztPQUdHO0lBQ0ksVUFBVSxDQUFDLE9BQWU7O1FBQy9CLE1BQUEsSUFBSSxDQUFDLE9BQU8sMENBQUUsVUFBVSxDQUFDLE9BQU8sRUFBRTtJQUNwQyxDQUFDO0lBRUQ7OztPQUdHO0lBQ0ksWUFBWSxDQUFDLFNBQWlCOztRQUNuQyxNQUFBLElBQUksQ0FBQyxPQUFPLDBDQUFFLFlBQVksQ0FBQyxTQUFTLEVBQUU7SUFDeEMsQ0FBQztJQUVEOzs7O09BSUc7SUFDSSxxQkFBcUI7O1FBQzFCLGFBQU8sSUFBSSxDQUFDLE9BQU8sMENBQUUsZ0JBQWdCLEdBQUc7SUFDMUMsQ0FBQztJQUVEOzs7Ozs7O09BT0c7SUFDSCxhQUFhLENBQUMsWUFBb0IsRUFBRSxPQUFnQjs7UUFDbEQsTUFBQSxJQUFJLENBQUMsT0FBTywwQ0FBRSxhQUFhLENBQUMsWUFBWSxFQUFFLE9BQU8sRUFBRTtJQUNyRCxDQUFDO0lBRUQ7OztPQUdHO0lBQ0ksVUFBVSxDQUFDLE9BQWU7O1FBQy9CLE1BQUEsSUFBSSxDQUFDLE9BQU8sMENBQUUsVUFBVSxDQUFDLE9BQU8sRUFBRTtJQUNwQyxDQUFDO0lBRUQ7Ozs7T0FJRztJQUNJLFFBQVEsQ0FBQyxPQUFlLEVBQUUsU0FBaUI7O1FBQ2hELE1BQUEsSUFBSSxDQUFDLE9BQU8sMENBQUUsUUFBUSxDQUFDLE9BQU8sRUFBRSxTQUFTLEVBQUU7SUFDN0MsQ0FBQztJQUVEOztPQUVHO0lBQ0ksUUFBUTs7UUFDYixhQUFPLElBQUksQ0FBQyxPQUFPLDBDQUFFLFFBQVEsR0FBRztJQUNsQyxDQUFDO0lBRUQ7OztPQUdHO0lBQ0ksUUFBUSxDQUFDLEtBQWM7O1FBQzVCLE1BQUEsSUFBSSxDQUFDLE9BQU8sMENBQUUsUUFBUSxDQUFDLEtBQUssRUFBRTtJQUNoQyxDQUFDO0lBRUQ7O09BRUc7SUFDSSxTQUFTOztRQUNkLGFBQU8sSUFBSSxDQUFDLE9BQU8sMENBQUUsU0FBUyxHQUFHO0lBQ25DLENBQUM7SUFFRDs7O09BR0c7SUFDSSxTQUFTLENBQUMsV0FBbUI7O1FBQ2xDLE1BQUEsSUFBSSxDQUFDLE9BQU8sMENBQUUsU0FBUyxDQUFDLFdBQVcsRUFBRTtJQUN2QyxDQUFDO0lBRUQ7O09BRUc7SUFDSSxVQUFVOztRQUNmLGFBQU8sSUFBSSxDQUFDLE9BQU8sMENBQUUsVUFBVSxHQUFHO0lBQ3BDLENBQUM7SUFFRDs7T0FFRztJQUNILFlBQVk7O1FBQ1YsYUFBTyxJQUFJLENBQUMsT0FBTywwQ0FBRSxZQUFZLEdBQUc7SUFDdEMsQ0FBQztJQUVEOztPQUVHO0lBQ0ksY0FBYzs7UUFDbkIsYUFBTyxJQUFJLENBQUMsT0FBTywwQ0FBRSxjQUFjLEdBQUc7SUFDeEMsQ0FBQztJQUVEOztPQUVHO0lBQ0ksV0FBVzs7UUFDaEIsYUFBTyxJQUFJLENBQUMsT0FBTywwQ0FBRSxXQUFXLEdBQUc7SUFDckMsQ0FBQztJQUVEOztPQUVHO0lBQ0ksUUFBUTs7UUFDYixhQUFPLElBQUksQ0FBQyxPQUFPLDBDQUFFLFFBQVEsR0FBRztJQUNsQyxDQUFDO0lBRUQ7O09BRUc7SUFDSSxZQUFZOztRQUNqQixhQUFPLElBQUksQ0FBQyxPQUFPLDBDQUFFLFlBQVksR0FBRztJQUN0QyxDQUFDO0lBRUQ7O09BRUc7SUFDSSxVQUFVOztRQUNmLGFBQU8sSUFBSSxDQUFDLE9BQU8sMENBQUUsVUFBVSxHQUFHO0lBQ3BDLENBQUM7SUFFRDs7T0FFRztJQUNJLFFBQVE7O1FBQ2IsYUFBTyxJQUFJLENBQUMsT0FBTywwQ0FBRSxRQUFRLEdBQUc7SUFDbEMsQ0FBQztJQUVEOztPQUVHO0lBQ0ksUUFBUTs7UUFDYixhQUFPLElBQUksQ0FBQyxPQUFPLDBDQUFFLFFBQVEsR0FBRztJQUNsQyxDQUFDO0lBRUQ7O09BRUc7SUFDSSxhQUFhOztRQUNsQixhQUFPLElBQUksQ0FBQyxPQUFPLDBDQUFFLGFBQWEsR0FBRztJQUN2QyxDQUFDO0lBRUQ7Ozs7T0FJRztJQUNJLGdCQUFnQixDQUNyQixLQUF3QixFQUN4QixRQUFvQjs7UUFFcEIsTUFBQSxJQUFJLENBQUMsT0FBTywwQ0FBRSxnQkFBZ0IsQ0FBQyxLQUFLLENBQUMsUUFBUSxFQUFFLEVBQUUsUUFBUSxFQUFFO0lBQzdELENBQUM7Q0FDRiJ9